import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Patient {
  id: string;
  customer_id: string;
  created_at?: string;
}

interface CreatePatientData {
  id: string;
  customer_id: string;
}

export const usePatients = (customerId?: string) => {
  return useQuery({
    queryKey: ["patients", customerId],
    queryFn: async () => {
      let query = supabase.from("patients").select("*");

      if (customerId) {
        query = query.eq("customer_id", customerId);
      }

      const { data, error } = await query.order("created_at", {
        ascending: false,
      });

      if (error) throw error;
      return data || [];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useCreatePatient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (patientData: CreatePatientData) => {
      const { data, error } = await supabase.from("patients").insert(patientData).select().single();

      if (error) throw error;
      return data as Patient;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patients"] });
      toast.success("Patient created successfully");
    },
    onError: (error) => {
      console.error("Error creating patient:", error);
      toast.error("Failed to create patient");
    },
  });
};
